// Tab 6 · In the app (R-1, retire the old Hub): the old admin's Lender → In the app tab on the new portal, the same tables
// and rules (api2/src/modules/in-app). Since PF-3 the facts the lender's page has (amounts, tenures, ages, income, rates, APR,
// fee, grievance officer, bank / NBFC) are only shown here, read from tab 1 and tab 2; this tab asks what the app alone
// needs (work types, PIN codes, charges, cooling-off, how offers are asked), the KFS, the handover, the checklist, the switch.
// Editors fill it in; reviewers / admins run the checks and sign off; an admin other than the one who signed off presses
// Live in app. The app is shown only the signed-off version: a change to a live lender reaches it with Publish changes.
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, Circle, ExternalLink, Loader2, Pause, Play, Rocket, Send, ShieldCheck, Upload, XCircle } from 'lucide-react'
import { Badge, ConfirmDialog, PromptDialog, formatDateTime, notify } from '@/kit/ui'
import { ApiError } from '@/lib/hubApi'
import { BUTTONS, KFS_MAX_BYTES, LOGO_MAX_BYTES, WORK_TYPES, inApp, productBody, type AppCheck, type InApp } from '@/lib/inApp'
import { useCurrentUser } from '@/shell/useCurrentUser'
import type { Lender } from '@/types'

const STATUS: Record<InApp['lender']['appStatus'], [string, 'success' | 'warning' | 'gray']> = { live: ['live in the app', 'success'], paused: ['paused in the app', 'warning'], off: ['not in the app', 'gray'] }

const rupees = (n: unknown) => (typeof n === 'number' ? `₹${n.toLocaleString('en-IN')}` : null)
const set = (x: unknown) => x !== null && x !== undefined
/** "from – to"; with one end missing, the end that is there and which one is not filled in. */
const range = (a: unknown, b: unknown, show: (x: unknown) => string | null) =>
  set(a) && set(b) ? `${show(a)} – ${show(b)}` : set(b) ? `up to ${show(b)} · lowest: not filled in` : set(a) ? `from ${show(a)} · highest: not filled in` : null
/** Where a missing fact is filled in (the rest are this tab's own fields). */
const WHERE: Record<string, string> = {
  kind: 'tab 1', grievance_name: 'tab 1', grievance_email: 'tab 1',
  ...Object.fromEntries(['amount_min', 'amount_max', 'tenures', 'age_min', 'age_max', 'rate_from', 'rate_to', 'apr_from', 'apr_to', 'processing_fee_text'].map((k) => [k, 'tab 2 · Products'])),
}
const pct = (x: unknown) => `${String(x)}%`
/** The facts the app reads from the lender's page (PF-3), as tab 6 shows them. */
const FACT_ROWS: [string, (d: InApp) => string | null][] = [
  ['Bank / NBFC', (d) => (d.lender.kind ? d.lender.kind.toUpperCase() : null)],
  ['Amount', (d) => range(d.facts.amount_min, d.facts.amount_max, rupees)],
  ['Tenures offered (months)', (d) => (Array.isArray(d.facts.tenures) ? (d.facts.tenures as number[]).join(', ') : null)],
  ['Age', (d) => range(d.facts.age_min, d.facts.age_max, (x) => String(x))],
  ['Lowest monthly income', (d) => rupees(d.facts.income_min_monthly) ?? 'any'],
  ['Interest rate (a year)', (d) => range(d.facts.rate_from, d.facts.rate_to, pct)],
  ['APR', (d) => range(d.facts.apr_from, d.facts.apr_to, pct)],
  ['Processing fee', (d) => (d.facts.processing_fee_text ? String(d.facts.processing_fee_text) : null)],
  ['Grievance officer', (d) => (d.facts.grievance_name ? [d.facts.grievance_name, d.facts.grievance_email, d.facts.grievance_phone].filter(Boolean).join(' · ') : null)],
]

const Icon = ({ c }: { c: AppCheck }) =>
  c.result === 'pass' ? <CheckCircle2 size={16} className="shrink-0 text-emerald-600" />
    : c.result === 'fail' ? <XCircle size={16} className="shrink-0 text-red-600" />
      : <Circle size={16} className="shrink-0 text-slate-300 dark:text-slate-600" />

/** What went wrong, in words: the API's message, and each field's problem. */
function Problem({ error }: { error: ApiError | Error | null }) {
  if (!error) return null
  const fields = error instanceof ApiError ? (error.extra.fields as { field: string; message: string }[] | undefined) : undefined
  return (
    <div className="rounded border-l-[3px] border-red-500 bg-red-50 px-3 py-2 text-xs text-red-800 dark:bg-red-950/30 dark:text-red-200" role="alert" data-testid="in-app-problem">
      {fields?.length ? <ul className="list-disc pl-4">{fields.map((f) => <li key={f.field}><b>{f.field.replace(/_/g, ' ')}</b> {f.message}</li>)}</ul> : error.message}
    </div>
  )
}

export function InAppTab({ lender }: { lender: Lender }) {
  const { can } = useCurrentUser()
  const slug = lender.slug
  const [d, setD] = useState<InApp | null>(null)
  const [loadError, setLoadError] = useState<Error | null>(null)
  const [problem, setProblem] = useState<Error | null>(null)
  const [busy, setBusy] = useState('')
  const [asking, setAsking] = useState<'' | 'sign' | 'live' | 'publish' | 'pause'>('')
  const [kfs, setKfs] = useState<File | null>(null)
  const [kfsVersion, setKfsVersion] = useState('')
  const [logo, setLogo] = useState<File | null>(null)
  const [pinMode, setPinMode] = useState<string | null>(null)
  // the form's boxes start from the answer; a new answer (save, upload) remounts the form
  const [formKey, setFormKey] = useState(0)

  const load = useCallback(() => {
    if (!inApp.available()) return
    inApp.get(slug).then((x) => { setD(x); setLoadError(null) }, (e: Error) => setLoadError(e))
  }, [slug])
  useEffect(load, [load])

  if (!inApp.available()) return <div className="card text-xs text-muted" data-testid="in-app-needs-api">In the app needs the Lender Hub API: it edits what the web app's catalog shows (not on the test fixtures).</div>
  if (loadError) return <Problem error={loadError} />
  if (!d) return <div className="card text-xs text-muted"><Loader2 size={13} className="mr-1 inline animate-spin" /> Loading…</div>

  const p = d.product
  const editable = can('app.edit')
  const v = (k: string) => (p[k] === null || p[k] === undefined ? '' : String(p[k]))
  const automatic = d.checks.filter((c) => c.name !== 'sign_off')
  const allPass = automatic.every((c) => c.result === 'pass')
  const mode = pinMode ?? String(p.pincode_mode ?? 'all')
  const [statusLabel, statusTone] = STATUS[d.lender.appStatus]

  const act = async (kind: string, run: () => Promise<InApp>, ok?: [string, string?]) => {
    setBusy(kind)
    try {
      const next = await run()
      setD(next)
      setProblem(null)
      if (ok) notify.success(...ok)
      return next
    } catch (e) {
      setProblem(e as Error)
      return null
    } finally {
      setBusy('')
    }
  }

  const save = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const body = productBody(new FormData(e.currentTarget))
    if (await act('save', () => inApp.saveProduct(slug, body), ['Saved', 'Run the checks again before going live.'])) { setPinMode(null); setFormKey((k) => k + 1) }
  }
  const saveHandover = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const texts = Object.fromEntries(BUTTONS.map(([k]) => [k, String(fd.get(`btn_${k}`) ?? '').trim()]).filter(([, t]) => t))
    const h = { web_app_url: String(fd.get('web_app_url') ?? '').trim() || null, app_android_url: String(fd.get('app_android_url') ?? '').trim() || null, app_ios_url: String(fd.get('app_ios_url') ?? '').trim() || null, button_texts: texts }
    await act('handover', () => inApp.saveHandover(slug, h), ['Handover saved', 'A new product version: sign off and publish it.'])
  }

  return (
    <div className="space-y-3" data-testid="in-app">
      <div className="card flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-0.5 text-xs">
          <p className="flex items-center gap-2 font-medium">In the web app <Badge tone={statusTone}>{statusLabel}</Badge></p>
          <p className="text-muted" data-testid="in-app-version">
            Product version {d.version}
            {d.lender.appLiveAt && <> · live since {formatDateTime(d.lender.appLiveAt)}</>}
            {d.lender.appStatus === 'paused' && d.lender.appPausedAt && <> · paused {formatDateTime(d.lender.appPausedAt)}</>}
          </p>
          {d.publishedVersion !== null && d.publishedAt && <p className="text-muted">The app shows version {d.publishedVersion} (since {formatDateTime(d.publishedAt)})</p>}
          {d.changesNotPublished && <p className="text-amber-700 dark:text-amber-300" data-testid="in-app-unpublished">Changes since the live version aren't in the app yet: Publish changes.</p>}
        </div>
        {can('app.status') && (
          <div className="flex flex-wrap gap-2">
            {d.changesNotPublished && (
              <button type="button" className="btn btn-primary" disabled={!!busy || !allPass} title={allPass ? undefined : 'Every check must pass first'} onClick={() => setAsking('publish')}>
                {busy === 'publish' ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Publish changes
              </button>
            )}
            {d.lender.appStatus !== 'live' && (
              <button type="button" className="btn btn-primary" disabled={!!busy || !allPass} title={allPass ? undefined : 'Every check must pass first'} onClick={() => setAsking('live')} data-testid="in-app-live">
                {busy === 'live' ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} />} {d.lender.appStatus === 'paused' ? 'Live in app again' : 'Live in app'}
              </button>
            )}
            {d.lender.appStatus === 'live' && <button type="button" className="btn btn-danger" disabled={!!busy} onClick={() => setAsking('pause')}><Pause size={13} /> Pause in app</button>}
          </div>
        )}
      </div>
      <Problem error={problem} />

      <section className="card space-y-2" aria-label="From the lender's page" data-testid="in-app-facts">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="section-title !mb-0">From the lender's page <span className="font-normal text-muted">(read here, changed there)</span></h3>
          <span className="flex gap-2 text-2xs"><Link to={`/lenders/${slug}/products`}>Edit in tab 2 · Products</Link><Link to={`/lenders/${slug}/info`}>Edit in tab 1</Link></span>
        </div>
        <p className="text-2xs text-muted">{d.factsFrom ? <>From the product <b>{d.factsFrom.name}</b> (<code>{d.factsFrom.slug}</code>) and tab 1. A change there is a new version here: sign off and publish again.</> : <>No active product in tab 2 yet: add one, then its facts show here.</>}</p>
        <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
          {FACT_ROWS.map(([label, show]) => {
            const value = show(d)
            return <div key={label} className="flex justify-between gap-2 border-b border-slate-100 py-0.5 dark:border-slate-800"><dt className="text-muted">{label}</dt><dd className={value ? '' : 'text-amber-700 dark:text-amber-300'}>{value ?? 'not filled in'}</dd></div>
          })}
        </dl>
      </section>

      <form key={`p${formKey}`} className="space-y-3" onSubmit={save} aria-label="App product">
        <fieldset disabled={!editable || busy === 'save'} className="space-y-3">
          <section className="card space-y-3">
            <h3 className="section-title">Who the app sends</h3>
            <fieldset><legend className="label">Work types</legend>
              <div className="flex flex-wrap gap-3">{WORK_TYPES.map(([k, l]) => (
                <label key={k} className="flex items-center gap-1.5 text-xs"><input type="checkbox" name="work_types" value={k} defaultChecked={(p.work_types as string[] | null)?.includes(k)} /> {l}</label>
              ))}</div>
            </fieldset>
            <div className="grid gap-3 sm:grid-cols-3">
              <div><label className="label" htmlFor="ia-pinmode">PIN codes</label>
                <select id="ia-pinmode" name="pincode_mode" className="input" value={mode} onChange={(e) => setPinMode(e.target.value)}>
                  <option value="all">Every PIN code</option><option value="allow">Only these</option><option value="block">All but these</option>
                </select></div>
              {mode !== 'all' && (
                <div className="sm:col-span-2"><label className="label" htmlFor="ia-pins">PIN codes (6 digits) or prefixes (2–5 digits), separated by spaces or commas</label>
                  <textarea id="ia-pins" name="pincode_list" rows={3} className="input font-mono" defaultValue={((p.pincode_list as string[] | null) ?? []).join(' ')} /></div>
              )}
            </div>
          </section>

          <section className="card space-y-3">
            <h3 className="section-title">Charges (as the KFS shows them)</h3>
            <div className="grid gap-3 sm:grid-cols-4">
              <div><label className="label" htmlFor="ia-feep">Highest fee (%, optional)</label><input id="ia-feep" name="processing_fee_pct_max" type="number" step="0.01" className="input" defaultValue={v('processing_fee_pct_max')} /></div>
              <div><label className="label" htmlFor="ia-cool">Cooling-off (days)</label><input id="ia-cool" name="cooling_off_days" type="number" className="input" defaultValue={v('cooling_off_days')} /></div>
              <div className="sm:col-span-2"><label className="label" htmlFor="ia-penal">Penal charges</label><input id="ia-penal" name="penal_charges_text" className="input" placeholder="₹500 per missed EMI" defaultValue={v('penal_charges_text')} /></div>
              <div className="sm:col-span-2"><label className="label" htmlFor="ia-fore">Foreclosure (optional)</label><input id="ia-fore" name="foreclosure_text" className="input" defaultValue={v('foreclosure_text')} /></div>
            </div>
          </section>

          <section className="card space-y-3">
            <h3 className="section-title">Offers in the app</h3>
            <fieldset className="space-y-1.5"><legend className="label">How offers are asked</legend>
              <label className="flex items-start gap-2 text-xs"><input type="radio" name="offer_mode" value="soft_api" defaultChecked={p.offer_mode === 'soft_api'} /> <span><b>Soft offer from the lender</b>: the Gateway asks the lender (a soft check) for a real offer</span></label>
              <label className="flex items-start gap-2 text-xs"><input type="radio" name="offer_mode" value="indicative" defaultChecked={p.offer_mode === 'indicative'} /> <span><b>Indicative only</b>: "Up to ₹X" from these rules; nothing goes to the lender before an application</span></label>
            </fieldset>
            <div><label className="label" htmlFor="ia-int">Gateway integration (offer checks and applications; the handover belongs to it)</label>
              <select id="ia-int" name="integration_id" className="input" defaultValue={v('integration_id')}>
                <option value="">Choose…</option>
                {d.integrations.map((i) => <option key={i.integrationId} value={i.integrationId}>{i.integrationId} · {i.title}{i.hasHandover ? '' : ' (no handover yet)'}</option>)}
                {!!p.integration_id && !d.integrations.some((i) => i.integrationId === p.integration_id) && <option value={v('integration_id')}>{v('integration_id')} (not in the Gateway now)</option>}
              </select>
              {d.gatewayError && <p className="field-error">The Gateway can't be reached: {d.gatewayError}</p>}
            </div>
          </section>
        </fieldset>
        {d.missing.length > 0 && <p className="text-2xs text-muted" data-testid="in-app-missing">Still to fill in: {d.missing.map((m) => `${m.replace(/_/g, ' ')}${WHERE[m] ? ` (${WHERE[m]})` : ''}`).join(', ')}.</p>}
        {editable ? <button type="submit" className="btn btn-primary" disabled={!!busy} data-testid="in-app-save">{busy === 'save' ? 'Saving…' : 'Save'}</button> : <p className="text-xs text-muted">Only an editor or admin can change these.</p>}
      </form>

      <form key={`h${formKey}-${d.version}`} className="card space-y-3" onSubmit={saveHandover} aria-label="Handover">
        <h3 className="section-title">Handover (the links to the lender after an approval)</h3>
        <p className="text-2xs text-muted">For <b>{v('integration_id') || 'the Gateway integration chosen above'}</b>. Empty links = the link in the lender's own answer. Each change is a new product version (sign off and publish again). Was the old Hub's Journeys tab.</p>
        <fieldset disabled={!editable || !p.integration_id || busy === 'handover'} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <div><label className="label" htmlFor="ia-web">Web link (https)</label><input id="ia-web" name="web_app_url" className="input" placeholder="https://" defaultValue={d.handover?.web_app_url ?? ''} /></div>
            <div><label className="label" htmlFor="ia-android">Android app link (https)</label><input id="ia-android" name="app_android_url" className="input" placeholder="https://play.google.com/…" defaultValue={d.handover?.app_android_url ?? ''} /></div>
            <div><label className="label" htmlFor="ia-ios">iPhone app link (https)</label><input id="ia-ios" name="app_ios_url" className="input" placeholder="https://apps.apple.com/…" defaultValue={d.handover?.app_ios_url ?? ''} /></div>
          </div>
          <fieldset><legend className="label">Button texts (optional, 40 characters at most)</legend>
            <div className="grid gap-2 sm:grid-cols-4">{BUTTONS.map(([k, l]) => (
              <div key={k}><label className="label" htmlFor={`ia-btn-${k}`}>{l}</label><input id={`ia-btn-${k}`} name={`btn_${k}`} className="input" maxLength={40} defaultValue={d.handover?.button_texts[k] ?? ''} /></div>
            ))}</div>
          </fieldset>
        </fieldset>
        {editable && <button type="submit" className="btn btn-secondary" disabled={!!busy || !p.integration_id} title={p.integration_id ? undefined : 'Choose the integration and save the product first'} data-testid="in-app-handover-save">{busy === 'handover' ? 'Saving…' : 'Save handover'}</button>}
      </form>

      <section className="card space-y-3">
        <h3 className="section-title">Key Fact Statement and logo</h3>
        {!d.storage && <p className="field-error">File storage (hub-media) isn't set up on this server.</p>}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <p className="text-xs" data-testid="in-app-kfs">
              {d.kfs ? <>Current KFS: <b>{d.kfs.version}</b>{d.kfs.uploadedAt && <>, {formatDateTime(d.kfs.uploadedAt)}</>} · <a href={inApp.fileUrl(slug, 'kfs')} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1">open <ExternalLink size={11} /></a></> : 'No KFS uploaded yet.'}
            </p>
            {editable && <>
              <div><label className="label" htmlFor="ia-kfsv">Version, as the lender names it</label><input id="ia-kfsv" className="input" value={kfsVersion} onChange={(e) => setKfsVersion(e.target.value)} placeholder="KFS-2026.1" /></div>
              <div><label className="label" htmlFor="ia-kfsfile">KFS (PDF, {Math.round(KFS_MAX_BYTES / 1024 / 1024)} MB at most)</label><input id="ia-kfsfile" type="file" accept="application/pdf" className="text-xs" disabled={!d.storage} onChange={(e) => setKfs(e.target.files?.[0] ?? null)} /></div>
              <p className="text-2xs text-muted">Earlier versions stay stored: the KFS a person saw can always be shown.</p>
              <button type="button" className="btn btn-secondary" disabled={!kfs || !kfsVersion.trim() || !!busy} onClick={() => void act('kfs', () => inApp.uploadKfs(slug, kfs!, kfsVersion.trim()), ['KFS uploaded']).then((ok) => { if (ok) { setKfs(null); setKfsVersion('') } })}>
                {busy === 'kfs' ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload KFS
              </button>
            </>}
          </div>
          <div className="space-y-2">
            {d.lender.hasLogo
              ? <img src={inApp.fileUrl(slug, 'logo', d.version)} alt={`${d.lender.name} logo`} className="h-12 w-auto rounded border border-slate-200 bg-white p-1" />
              : <p className="text-xs text-muted">No logo yet (the app shows the lender's initials).</p>}
            {editable && <>
              <div><label className="label" htmlFor="ia-logofile">Logo (PNG, JPEG or WebP, {Math.round(LOGO_MAX_BYTES / 1024)} KB at most)</label><input id="ia-logofile" type="file" accept="image/png,image/jpeg,image/webp" className="text-xs" disabled={!d.storage} onChange={(e) => setLogo(e.target.files?.[0] ?? null)} /></div>
              <button type="button" className="btn btn-secondary" disabled={!logo || !!busy} onClick={() => void act('logo', () => inApp.uploadLogo(slug, logo!), ['Logo uploaded']).then((ok) => { if (ok) setLogo(null) })}>
                {busy === 'logo' ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload logo
              </button>
            </>}
          </div>
        </div>
      </section>

      <section className="card space-y-3">
        <h3 className="section-title">App checklist</h3>
        <p className="text-2xs text-muted">Live in app needs every check passed and a sign-off on this product version, by someone other than the admin who presses it. Any change to the product, the files or the handover needs a new sign-off; once live, it reaches the app with Publish changes.</p>
        <ol className="divide-y divide-slate-100 rounded-md border border-slate-200 dark:divide-slate-800 dark:border-slate-800" data-testid="in-app-checks">
          {d.checks.map((c, i) => (
            <li key={c.name} className="flex gap-2.5 px-3 py-2" data-check={c.name} data-result={c.result ?? 'none'}>
              <Icon c={c} />
              <div className="min-w-0">
                <p className="text-xs font-medium">{i + 1}. {c.label}</p>
                <p className={`break-words text-2xs ${c.result === 'fail' ? 'text-red-600 dark:text-red-400' : 'text-muted'}`}>
                  {c.detail}{c.signedOffBy ? ` · by ${c.signedOffBy}` : ''}{c.checkedAt ? ` · ${formatDateTime(c.checkedAt)}` : ''}
                </p>
              </div>
            </li>
          ))}
        </ol>
        {can('app.check') && (
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-secondary" disabled={!!busy} onClick={() => void act('run', () => inApp.run(slug))} data-testid="in-app-run">
              {busy === 'run' ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}{busy === 'run' ? 'Checking… (may include a sandbox call)' : 'Run all checks'}
            </button>
            <button type="button" className="btn btn-secondary" disabled={!!busy || !allPass} title={allPass ? undefined : 'Every automatic check must pass first'} onClick={() => setAsking('sign')} data-testid="in-app-sign">
              <ShieldCheck size={13} /> Sign off
            </button>
          </div>
        )}
      </section>

      <PromptDialog
        isOpen={asking === 'sign'}
        title="Sign off"
        message="What did you review? (the product, the KFS, the grievance officer). Kept with the sign-off and in the audit log."
        inputLabel="Note"
        placeholder="Checked the product against the KFS"
        confirmText="Sign off"
        validate={(x) => (x.trim().length > 300 ? 'At most 300 characters' : null)}
        onConfirm={(note) => { setAsking(''); void act('sign', () => inApp.signOff(slug, note), ['Signed off', 'A different admin can now press Live in app.']) }}
        onClose={() => setAsking('')}
      />
      <ConfirmDialog
        isOpen={asking === 'live'}
        onClose={() => setAsking('')}
        onConfirm={() => { setAsking(''); void act('live', () => inApp.live(slug), [`${d.lender.name} is live in the app`, 'The app shows it within 5 minutes.']) }}
        title={`Put ${d.lender.name} live in the app?`}
        message="Every check runs again first. Then the web app can match people with this lender and show its offers."
        confirmText="Live in app"
        type="warning"
      />
      <ConfirmDialog
        isOpen={asking === 'publish'}
        onClose={() => setAsking('')}
        onConfirm={() => { setAsking(''); void act('publish', () => inApp.publish(slug), ['Changes published', 'The app shows them within 5 minutes.']) }}
        title={`Publish version ${d.version} of ${d.lender.name} to the app?`}
        message={`Every check runs again first. The app then shows this version instead of version ${d.publishedVersion ?? '–'}.`}
        confirmText="Publish changes"
        type="warning"
      />
      <PromptDialog
        isOpen={asking === 'pause'}
        title={`Pause ${d.lender.name} in the app?`}
        message="The app stops showing this lender. Applications already sent keep their status. Kept in the audit log."
        inputLabel="Reason"
        placeholder="e.g. the lender asked to stop for the weekend"
        confirmText="Pause in app"
        validate={(x) => (x.trim().length < 3 ? 'Say why (at least 3 characters)' : x.length > 200 ? 'At most 200 characters' : null)}
        onConfirm={(reason) => { setAsking(''); void act('pause', () => inApp.pause(slug, reason.trim()), ['Paused in the app']) }}
        onClose={() => setAsking('')}
      />
    </div>
  )
}
